import { Badge } from "@/common/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";

import { formatPolicyName } from "./format";
import type { ActionBreakdown, PolicyBreakdown, SourceBreakdown } from "./guardrail-metrics-types";

interface BreakdownCardsProps {
  byPolicy: PolicyBreakdown[];
  bySource: SourceBreakdown[];
  byAction: ActionBreakdown[];
}

function barWidth(count: number, max: number): string {
  if (max === 0) return "0%";
  return `${Math.round((count / max) * 100)}%`;
}

export function BreakdownCards({
  byPolicy,
  bySource,
  byAction,
}: BreakdownCardsProps): React.ReactElement {
  const maxPolicyCount = Math.max(...byPolicy.map((p) => p.count), 1);

  return (
    <div data-testid="breakdown-cards" className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <Card data-testid="policy-breakdown-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">By Policy</CardTitle>
        </CardHeader>
        <CardContent>
          {byPolicy.length === 0 ? (
            <p className="text-sm text-muted-foreground">No interventions</p>
          ) : (
            <div className="space-y-3">
              {byPolicy.map((item) => (
                <div key={item.policy} data-testid={`policy-row-${item.policy}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span>{formatPolicyName(item.policy)}</span>
                    <span className="font-mono text-xs text-muted-foreground">{item.count}</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full bg-destructive/70"
                      style={{ width: barWidth(item.count, maxPolicyCount) }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="source-breakdown-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">By Source</CardTitle>
        </CardHeader>
        <CardContent>
          {bySource.length === 0 ? (
            <p className="text-sm text-muted-foreground">No interventions</p>
          ) : (
            <div className="space-y-2">
              {bySource.map((item) => (
                <div
                  key={item.source}
                  data-testid={`source-row-${item.source}`}
                  className="flex items-center justify-between"
                >
                  <Badge variant={item.source === "input" ? "default" : "secondary"}>
                    {item.source}
                  </Badge>
                  <span className="font-mono text-sm">{item.count}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="action-breakdown-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">By Action</CardTitle>
        </CardHeader>
        <CardContent>
          {byAction.length === 0 ? (
            <p className="text-sm text-muted-foreground">No interventions</p>
          ) : (
            <div className="space-y-2">
              {byAction.map((item) => (
                <div
                  key={item.action}
                  data-testid={`action-row-${item.action}`}
                  className="flex items-center justify-between"
                >
                  <Badge variant="destructive">{item.action}</Badge>
                  <span className="font-mono text-sm">{item.count}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
